<script lang="ts">
    import { slide } from 'svelte/transition'
    import IconifyIcon from '@iconify/svelte'
    import downIcon from '@iconify/icons-fa-solid/chevron-down'
    import editIcon from '@iconify/icons-fa-solid/cog'
    import deleteIcon from '@iconify/icons-fa-solid/trash-alt'
    import {createEventDispatcher} from 'svelte'
    import {getDefaultValue} from 'src/components/cabinet/app-configs/components/helpers'
    import type {ValueType} from 'src/components/cabinet/app-configs/components/helpers'
    import { Tooltip } from 'sveltestrap';

    const dispatch = createEventDispatcher()

    export let key
    export let value
    export let level
    export let status = false
    export let readonly = true
    export let searchText
    const searchRegex = new RegExp(searchText, 'i')
    export let allowTypes = ['array', 'string', 'number', 'boolean']

    let variableIsEdit = false
    let keyId = Math.random().toString(16).slice(2)

    function typeToString(type: ValueType): string {
        switch (type) {
            case 'number':
                return 'Number'
            case 'boolean':
                return 'Checkbox'
            case 'string':
                return 'Text'
            case 'array':
                return 'List'
            case 'object':
                return 'Object'
            default:
                throw new Error(`Unknown value type: ${type}`)
        }
    }

    function onDelete() {
        dispatch('delete')
    }

    function getAvailableKey(arr) {
        let index = 0;
        while (arr.find((elem) => elem[0] === 'key' + index)) {
            index++;
        }
        return 'key' + index;
    }

    let prevValueType = value.type
    $: if (value.type !== prevValueType) {
        prevValueType = value.type
        value.value = getDefaultValue(value.type).value
    }

    function changeVariableType() {
        dispatch('changeType')
    }
</script>

<div class="form-group">
    <div class="input-group mb-1">
        {#if value.description}
            <Tooltip target={`key-${keyId}`} placement="left">{value.description}</Tooltip>
        {/if}
        <input type="text" class="form-control key-field" class:isFound={searchText && searchRegex.test(key)}
            placeholder="Key" bind:value="{key}" id="key-{keyId}" readonly={readonly}>
        {#if value.type === 'string'}
            <input type="text" class="form-control" placeholder="Enter text" bind:value="{value.value}">
        {:else if value.type === 'number'}
            <input type="number" class="form-control" placeholder="Enter number" bind:value="{value.value}">
        {:else if value.type === 'boolean'}
            <span class="input-group-text checkbox-wrapper">
                <input type="checkbox" class="form-check" bind:checked="{value.value}">
            </span>
        {:else if value.type === 'array'}
            <div class="form-control vertical-align-top accordion-icon" on:click={() => status = !status} class:opened={status}>
                <IconifyIcon icon={downIcon}/>
            </div>
        {/if}
        {#if !readonly}
            <button class="btn btn-secondary" on:click={() => variableIsEdit = !variableIsEdit}>
                <IconifyIcon icon={editIcon}/>
            </button>
        {/if}
    </div>
    {#if variableIsEdit && !readonly}
        <div class="input-group mb-1" transition:slide={{duration: 350}}>
            <input type="text" class="form-control" placeholder="Description" bind:value="{value.description}" readonly={readonly}>
            <select class="form-select" bind:value={value.type} on:change={changeVariableType}>
                {#each allowTypes as allowType}
                    <option value="{allowType}">{typeToString(allowType)}</option>
                {/each}
            </select>
            <button class="btn btn-danger" on:click={() => { onDelete(value) }}>
                <IconifyIcon icon={deleteIcon}/>
            </button>
        </div>
    {/if}
    {#if value != null && typeof value.value === 'object' && value.type === 'array' && status}
        <div class="accordion-body-wrapper" transition:slide={{duration: 350}}>
            {#each value.value as childVariable, index}
                {#if childVariable?.value}
                    <div class="variables-group mb-1">
                        {#if childVariable != null && typeof childVariable === 'object' && childVariable.type === 'object'}
                            {#each childVariable.value as entry, childVariableIndex}
                                <svelte:self
                                    level={level+=1}
                                    {searchText}
                                    bind:key={entry[0]}
                                    bind:value={entry[1]}
                                    {readonly}
                                    on:delete={() => {
                                        value.value[index].value.splice(childVariableIndex, 1)
                                        value = value
                                    }}
                                    on:changeType={() => {
                                        value = value
                                    }}
                                    allowTypes={level < 5 ? ['number', 'boolean', 'string', 'array'] : ['number', 'boolean', 'string']}
                                />
                            {/each}
                        {/if}
                        {#if !readonly}
                            <div class="row">
                                <div class="col-6">
                                    <button class="btn btn-success variable-btn w-100" on:click={() => {
                                        if (!childVariable.value) {
                                            childVariable.value = []
                                        }
                                        childVariable.value.push([getAvailableKey(childVariable.value), {
                                            type: 'string',
                                            description: '',
                                            value: ''
                                        }])
                                        childVariable = childVariable
                                    }}>Add variable</button>
                                </div>
                                <div class="col-3 pl-0">
                                    <button class="btn btn-success copy-group-btn w-100" on:click={() => {
                                        value.value.push(JSON.parse(JSON.stringify(childVariable)))
                                        value = value
                                    }}>Copy group</button>
                                </div>
                                <div class="col-3 pl-0">
                                    <button class="btn btn-danger w-100" on:click={() => {
                                        value.value.splice(index, 1)
                                        value = value
                                    }}>Delete group</button>
                                </div>
                            </div>
                        {/if}
                    </div>
                {/if}
            {/each}
            {#if !readonly}
                <div class="variables-group mb-1">
                    <button class="btn btn-success w-100" on:click={() => {
                        if (!value.value) {
                            value.value = []
                        }
                        value.value.push({
                            type: 'object',
                            value: []
                        })
                        value = value
                    }}>Add group</button>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    :global(.variables .form-control),
    :global(.variables .form-select),
    :global(.variables .btn) {
        font-size: 0.85rem !important;
        padding: 3px 6px !important;
        line-height: normal;
    }

    :global(.variables .form-check) {
        min-height: 14px;
    }

    :global(.variables .input-group-text .form-check) {
        width: 14px !important;
        height: 14px !important;
    }

    :global(.variables .btn-success) {
        max-width: 500px;
    }

    .accordion-body-wrapper {
        margin-left: 50%;
        margin-right: -50%;
        padding: 5px;
        border: 1px solid #ced4da;
        margin-bottom: 10px;
        border-radius: 5px;
        background: #f6f7fa;
        transition: max-height .35s ease-out;
    }

    .accordion-body-wrapper > .variables-group:first-child {
        margin-top: 5px;
    }

    .btn-danger {
        padding: 10px 14px;
        min-width: 41px;
    }

    .accordion-icon {
        font-size: 20px;
        line-height: 16px;
        color: #838f95;
        text-align: center;
        cursor: pointer;
        user-select: none;
    }

    .accordion-icon :global(svg) {
        transition: .25s;
    }

    .accordion-icon.opened :global(svg) {
        transform: rotate(180deg);
    }

    .form-select {
        max-width: 550px;
    }

    .checkbox-wrapper {
        flex: 1 1 auto;
        width: 1%;
        min-width: 0;
        padding: 5px 0.75rem 0;
    }

    .input-group-text .form-check {
        width: 18px;
        height: 18px;
        cursor: pointer;
    }

    .variables-group {
        border-radius: 4px;
        padding: 5px 0 5px 5px;
        border: 1px solid #ced4da;
        /*border-left: 10px solid rgba(251, 95, 33, 0.5);*/
    }

    :global(.main-variable-group .variables-group .variables-group) {
        /*border-left: 10px solid rgba(251, 95, 33, 0.8);*/
    }

    :global(.main-variable-group .variables-group .variables-group .variables-group) {
        /*border-left: 10px solid rgb(251, 95, 33);*/
    }

    :global(.main-variable-group .variables-group .variables-group .variables-group .variables-group) {
        /*border-left: 10px solid #d14912;*/
    }

    .variables-group .variables-group {
        /*border-left: 5px solid #c2c8cf;*/
    }

    .form-control.key-field {
        background: rgba(0, 150, 136, 0.1);
    }

    .btn.btn-success.variable-btn {
        background: #3eaea5;
    }

    .btn.btn-success.variable-btn:hover {
        background: #39a59c;
    }

    .btn.btn-success.copy-group-btn {
        background: #8391a2;
    }

    .btn.btn-success.copy-group-btn:hover {
        background: #7b8899;
    }

    .pl-0 {
        padding-left: 0;
    }

    .form-control:focus {
        box-shadow: none;
    }

    .key-field.isFound {
        background: rgba(255, 235, 59, 0.3);
    }

    .form-control.accordion-icon {
        background: #ced4da;
    }

    @media(max-width: 767px) {
        select.form-select {
            min-width: 100px;
        }
    }
</style>
